import { ApiError, InvalidApiResponseError } from "./errors";
import { getCsrfToken } from "../utils/csrf";
import { sanitizeNext } from "../sanitize-next";

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  /** Advisory/background requests must never own session navigation. */
  redirectOnUnauthorized?: boolean;
  /**
   * How to read a successful (2xx) response body. Defaults to "json".
   * "text" returns the raw `response.text()` verbatim (e.g. a text/csv export)
   * and "blob" returns a binary attachment. Neither success path calls
   * `response.json()`. Error responses (non-2xx) still read the JSON error
   * envelope regardless of this setting.
   */
  responseType?: "json" | "text" | "blob";
};

type ApiErrorBody = {
  code?: string;
  message?: string;
  detail?:
    | string
    | {
        code?: string;
        message?: string;
        details?: Record<string, unknown>;
      };
  details?: Record<string, unknown>;
};

export type BinaryDownload = {
  blob: Blob;
  contentType: string;
  filename: string;
};

const DEFAULT_ZIP_DOWNLOAD_FILENAME = "tournament-artifact.zip";
const DEFAULT_PDF_DOWNLOAD_FILENAME = "game-sheet.pdf";

function defaultBinaryDownloadFilename(contentType: string): string {
  if (contentType === "application/pdf") return DEFAULT_PDF_DOWNLOAD_FILENAME;
  if (contentType === "application/zip") return DEFAULT_ZIP_DOWNLOAD_FILENAME;
  const extension: Record<string, string> = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "text/plain": "txt",
    "text/csv": "csv",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "docx",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      "pptx",
  };
  return `class-file.${extension[contentType] ?? "bin"}`;
}

function attachmentFilename(
  contentDisposition: string | null,
  contentType: string,
): string {
  const match = contentDisposition?.match(
    /(?:^|;)\s*filename\s*=\s*(?:"([^"]*)"|([^;\s]*))/i,
  );
  const encoded = contentDisposition?.match(
    /(?:^|;)\s*filename\*\s*=\s*UTF-8''([^;\s]*)/i,
  )?.[1];
  let decoded = "";
  if (encoded) {
    try {
      decoded = decodeURIComponent(encoded);
    } catch {
      decoded = "";
    }
  }
  const candidate = (decoded || match?.[1] || match?.[2] || "").trim();
  const safeFilename = new RegExp(
    "^[A-Za-z0-9][A-Za-z0-9 ._()-]{0,127}\\.(?:jpg|jpeg|png|webp|pdf|txt|csv|docx|xlsx|pptx|zip)$",
    "i",
  );
  return safeFilename.test(candidate)
    ? candidate
    : defaultBinaryDownloadFilename(contentType);
}

/**
 * PUBLIC_PATH_PATTERNS — paths the BFF allows anonymous read access to
 * (must stay in sync with bff/src/proxy.ts PUBLIC_GET_PATHS).
 *
 * On a 401 from a public path the global interceptor MUST NOT hard-redirect
 * to /login — anonymous tournament browsing/detail/bracket views are valid
 * UX (SEO + share links). The page receives the ApiError and decides how to
 * surface it (e.g. "sign in to register").
 *
 * Patterns match the path passed to httpClient (no /bff/api prefix).
 */
const PUBLIC_PATH_PATTERNS: RegExp[] = [
  /^\/programs\/public\/?(\?.*)?$/,
  /^\/programs\/[0-9a-f-]{36}\/public\/?(\?.*)?$/,
  /^\/programs\/[0-9a-f-]{36}\/public\/(?:track-options|session-preview)\/?(\?.*)?$/,
  /^\/search(?:\/suggestions)?\/?(\?.*)?$/,
  /^\/tournaments\/?(\?.*)?$/, // browse
  /^\/tournaments\/[0-9a-f-]{36}\/?(\?.*)?$/, // detail
  /^\/tournaments\/[0-9a-f-]{36}\/archive\/?(\?.*)?$/, // published archive receipt
  /^\/tournaments\/[0-9a-f-]{36}\/archive\/release\/?(\?.*)?$/, // public report availability
  /^\/tournaments\/[0-9a-f-]{36}\/archive\/report\.pdf\/?(\?.*)?$/, // public official report
  /^\/tournaments\/[0-9a-f-]{36}\/prospectus\/?(\?.*)?$/, // public prospectus delivery
  /^\/tournaments\/[0-9a-f-]{36}\/events\/?(\?.*)?$/, // events
  /^\/tournaments\/[0-9a-f-]{36}\/players\/?(\?.*)?$/, // identity-safe entrants
  /^\/tournaments\/[0-9a-f-]{36}\/draws\/?(\?.*)?$/, // draws
  /^\/tournaments\/[0-9a-f-]{36}\/live\/?(\?.*)?$/, // public Live snapshot
  /^\/tournaments\/[0-9a-f-]{36}\/live\/public\/schedule\/?(\?.*)?$/, // public Live schedule
  /^\/tournaments\/[0-9a-f-]{36}\/live\/public\/events\/?(\?.*)?$/, // public Live SSE
  /^\/tournaments\/draws\/[0-9a-f-]{36}\/bracket\/?(\?.*)?$/, // bracket
  /^\/tournaments\/draws\/[0-9a-f-]{36}\/standings\/?(\?.*)?$/, // standings
  // PUBLIC guardian-consent confirm (#1684 / #1692) — a logged-out guardian POSTs
  // here with a signed token. On the (unexpected) 401 this must NOT hard-redirect
  // to /login: the guardian has no BW account, so the page surfaces the error
  // itself. The token is the credential; the BFF allowlists this path.
  /^\/tournaments\/entries\/guardian-consent\/confirm\/?$/,
  /^\/tournaments\/entries\/guardian-consent\/preview\/?(\?.*)?$/,
];

// A venue display (and the entrance scan kiosk) has a BFF-held, revocable
// capability session rather than a player login. A rejected display cookie is a terminal display state, not a
// reason to navigate a TV to the player sign-in screen.
const DISPLAY_SESSION_PATH_PATTERNS: RegExp[] = [
  /^\/tournaments\/display-capabilities\/exchange\/?$/,
  /^\/tournaments\/[0-9a-f-]{36}\/live\/display(?:\/events)?\/?(\?.*)?$/,
  // The entrance scan kiosk (#2691) has the same shape: a rejected kiosk
  // cookie means "pair this tablet again", never the player sign-in screen.
  /^\/kiosk\/(?:exchange|session|scans)\/?$/,
];

function isPublicPath(path: string): boolean {
  // Strip /bff/api prefix if present so patterns are written in backend
  // path-space (the same as the BFF allowlist).
  const normalized = path.startsWith("/bff/api")
    ? path.slice("/bff/api".length)
    : path;
  return PUBLIC_PATH_PATTERNS.some((re) => re.test(normalized));
}

function suppressesUnauthorizedNavigation(
  path: string,
  method: string,
): boolean {
  const normalized = path.startsWith("/bff/api")
    ? path.slice("/bff/api".length)
    : path;
  return (
    (method === "GET" && isPublicPath(path)) ||
    (method === "POST" &&
      /^\/tournaments\/entries\/guardian-consent\/(?:confirm|preview)\/?(\?.*)?$/.test(
        normalized,
      )) ||
    (method === "GET" &&
      /^\/draws\/[0-9a-f-]{36}\/team-matches\/?(\?.*)?$/.test(normalized)) ||
    DISPLAY_SESSION_PATH_PATTERNS.some((pattern) => pattern.test(normalized))
  );
}

function unauthorizedApiError(
  path: string,
  redirectOnUnauthorized: boolean,
  requestId: string | undefined,
  method: string,
): ApiError {
  if (
    redirectOnUnauthorized &&
    !suppressesUnauthorizedNavigation(path, method)
  ) {
    const currentPath = sanitizeNext(
      window.location.pathname + window.location.search,
    );
    // A request already failing on an auth page must not wrap that page in
    // another returnUrl. That can strand a new session on the same route.
    if (currentPath) {
      const returnUrl = encodeURIComponent(currentPath);
      window.location.href = `/login?returnUrl=${returnUrl}`;
    }
  }
  return new ApiError(
    401,
    "UNAUTHORIZED",
    "Session expired",
    undefined,
    requestId,
  );
}

function apiErrorFromBody(
  status: number,
  statusText: string,
  errorBody: ApiErrorBody,
  requestId: string | undefined,
): ApiError {
  let code = errorBody.code ?? "UNKNOWN_ERROR";
  let message = errorBody.message;
  let details = errorBody.details;

  if (typeof errorBody.detail === "string") {
    message ??= errorBody.detail;
  } else if (errorBody.detail && typeof errorBody.detail === "object") {
    code = errorBody.detail.code ?? code;
    message ??= errorBody.detail.message;
    details ??= errorBody.detail.details ?? errorBody.detail;
  }
  return new ApiError(status, code, message ?? statusText, details, requestId);
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const {
    body,
    headers: extraHeaders,
    responseType,
    redirectOnUnauthorized = true,
    ...rest
  } = options;

  const method = (rest.method ?? "GET").toUpperCase();

  const isStateChanging = ["POST", "PUT", "PATCH", "DELETE"].includes(method);

  const isFormData = body instanceof FormData;
  const headers: Record<string, string> = {
    // Only set Content-Type for methods that send a body
    ...(isStateChanging && !isFormData
      ? { "Content-Type": "application/json" }
      : {}),
    ...(extraHeaders as Record<string, string>),
  };

  // CSRF double-submit cookie — only on state-changing methods
  if (isStateChanging) {
    const csrf = getCsrfToken();
    if (csrf) {
      headers["X-CSRF-Token"] = csrf;
    }
  }

  // Prefix with /bff/api so requests go through the BFF proxy
  const url = path.startsWith("/bff/") ? path : `/bff/api${path}`;

  const response = await fetch(url, {
    ...rest,
    headers,
    credentials: "include",
    body:
      body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
  });
  const requestId = response.headers.get("X-Request-Id") ?? undefined;

  if (response.status === 401) {
    // Public read paths (tournament browse/detail/draws/bracket) are
    // anonymous-readable. A 401 there means the backend rejected something
    // unrelated to auth (e.g. malformed token); propagate as a normal
    // ApiError so the page can show its own state. Do NOT redirect — that
    // breaks SEO and share-link journeys for unauthenticated visitors.
    throw unauthorizedApiError(path, redirectOnUnauthorized, requestId, method);
  }

  if (!response.ok) {
    let errorBody: ApiErrorBody = {};
    try {
      errorBody = await response.json();
    } catch {
      // non-JSON error response
    }
    throw apiErrorFromBody(
      response.status,
      response.statusText,
      errorBody,
      requestId,
    );
  }

  if (response.status === 204) {
    if (responseType === "text") return "" as T;
    if (responseType === "blob") {
      return {
        blob: new Blob(),
        contentType: "application/octet-stream",
        filename: DEFAULT_ZIP_DOWNLOAD_FILENAME,
      } as T;
    }
    return undefined as T;
  }

  if (responseType === "text") {
    return (await response.text()) as T;
  }

  if (responseType === "blob") {
    const blob = await response.blob();
    const contentType =
      response.headers.get("content-type")?.split(";", 1)[0] ||
      blob.type ||
      "application/octet-stream";
    return {
      blob,
      contentType,
      filename: attachmentFilename(
        response.headers.get("content-disposition"),
        contentType,
      ),
    } as T;
  }

  try {
    return (await response.json()) as T;
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new InvalidApiResponseError(path);
    }
    throw error;
  }
}

/**
 * Authenticated multipart upload with browser-measured request progress.
 *
 * Fetch does not expose upload progress for a FormData request. Direct-message
 * attachments therefore use XMLHttpRequest only for this narrow transport
 * seam, retaining the same BFF prefix, cookie, CSRF, redirect, and typed-error
 * behavior as the standard client. The browser owns the multipart boundary.
 */
function requestFormWithUploadProgress<T>(
  path: string,
  body: FormData,
  onProgress: (percent: number) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const csrf = getCsrfToken();
    const url = path.startsWith("/bff/") ? path : `/bff/api${path}`;

    xhr.open("POST", url, true);
    xhr.withCredentials = true;
    if (csrf) xhr.setRequestHeader("X-CSRF-Token", csrf);

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || event.total <= 0) return;
      onProgress(
        Math.max(
          0,
          Math.min(100, Math.round((event.loaded / event.total) * 100)),
        ),
      );
    };
    xhr.onerror = () => reject(new TypeError("Network request failed"));
    xhr.onabort = () =>
      reject(new DOMException("Upload aborted", "AbortError"));
    xhr.onload = () => {
      const requestId =
        typeof xhr.getResponseHeader === "function"
          ? (xhr.getResponseHeader("X-Request-Id") ?? undefined)
          : undefined;
      if (xhr.status === 401) {
        reject(unauthorizedApiError(path, true, requestId, "POST"));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        let errorBody: ApiErrorBody = {};
        try {
          errorBody = JSON.parse(xhr.responseText) as ApiErrorBody;
        } catch {
          // Non-JSON error responses use the status text, like fetch requests.
        }
        reject(
          apiErrorFromBody(xhr.status, xhr.statusText, errorBody, requestId),
        );
        return;
      }
      if (xhr.status === 204) {
        resolve(undefined as T);
        return;
      }
      try {
        resolve(JSON.parse(xhr.responseText) as T);
      } catch {
        reject(new InvalidApiResponseError(path));
      }
    };
    xhr.send(body);
  });
}

export const httpClient = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "GET" }),

  // A GET that returns the raw response body as text (e.g. a text/csv export),
  // reusing every behaviour of the JSON path (prefix, credentials, CSRF, the
  // 401 interceptor, ApiError mapping) — but never calling response.json() on
  // the success body.
  getText: (path: string, options?: RequestOptions) =>
    request<string>(path, { ...options, method: "GET", responseType: "text" }),

  // A GET that returns a binary attachment through the same BFF, credential,
  // 401, and typed-error path as JSON/text reads. The server-provided filename
  // is accepted only when it is a conservative safe ZIP or PDF basename that
  // matches the response media type.
  getBlob: (path: string, options?: RequestOptions) =>
    request<BinaryDownload>(path, {
      ...options,
      method: "GET",
      responseType: "blob",
    }),

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "POST", body }),

  // Multipart requests use the same authenticated BFF + CSRF + typed-error
  // path as JSON mutations, while deliberately letting the browser provide the
  // boundary-bearing Content-Type header.
  postForm: <T>(path: string, body: FormData, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "POST", body }),

  postFormWithProgress: <T>(
    path: string,
    body: FormData,
    onProgress: (percent: number) => void,
  ) => requestFormWithUploadProgress<T>(path, body, onProgress),

  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PUT", body }),

  putForm: <T>(path: string, body: FormData, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PUT", body }),

  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PATCH", body }),

  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "DELETE" }),

  deleteWithBody: <T>(path: string, body: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "DELETE", body }),
};
